import {
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { FC } from 'react';
import { useGetList, useInput, useTranslate } from 'react-admin';
import { Hardware } from 'types/Hardware';

const HardwareCompatibilityInput: FC = () => {
  const translate = useTranslate();
  const { data: hardwareList, isLoading } = useGetList<Hardware>('hardware', {
    pagination: { page: 1, perPage: 200 },
    sort: { field: 'name', order: 'ASC' },
  });

  const {
    field: { value: hardwareIds = [], onChange: onHardwareIdsChange },
  } = useInput({ source: 'hardware_ids' });
  const {
    field: { value: requiredHardwareIds = [], onChange: onRequiredHardwareIdsChange },
  } = useInput({ source: 'required_hardware_ids' });

  if (isLoading) return <CircularProgress size={24} />;

  const toggleCompatible = (id: number) => {
    if (hardwareIds.includes(id)) {
      onHardwareIdsChange(hardwareIds.filter((hardwareId: number) => hardwareId !== id));
      onRequiredHardwareIdsChange(requiredHardwareIds.filter((hardwareId: number) => hardwareId !== id));
    } else {
      onHardwareIdsChange([...hardwareIds, id]);
    }
  };

  // Only reachable for ids already in hardwareIds — the checkbox is disabled otherwise.
  const toggleRequired = (id: number) => {
    if (requiredHardwareIds.includes(id)) {
      onRequiredHardwareIdsChange(requiredHardwareIds.filter((hardwareId: number) => hardwareId !== id));
    } else {
      onRequiredHardwareIdsChange([...requiredHardwareIds, id]);
    }
  };

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{translate('resources.software_components.fields.hardware_ids')}</TableCell>
            <TableCell>{translate('custom.labels.compatible')}</TableCell>
            <TableCell>{translate('custom.labels.required')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {hardwareList?.map((hardware) => (
            <TableRow key={hardware.id}>
              <TableCell>{hardware.name}</TableCell>
              <TableCell>
                <Checkbox checked={hardwareIds.includes(hardware.id)} onChange={() => toggleCompatible(hardware.id)} />
              </TableCell>
              <TableCell>
                <Checkbox
                  checked={requiredHardwareIds.includes(hardware.id)}
                  disabled={!hardwareIds.includes(hardware.id)}
                  onChange={() => toggleRequired(hardware.id)}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default HardwareCompatibilityInput;
